import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { InboundFaxDto, InboxDto, RestrictionDto } from '@app/core/model';
import { InboundFaxApiService, LookupService, NotificationService } from '@app/core/service';
import { FaxDestination, InfoMessage } from '@app/shared/constants';
import { InboundFaxesTableModel } from '@app/shared/models';
import { SelectItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { RadioButtonModule } from 'primeng/radiobutton';
import { TagModule } from 'primeng/tag';
import { finalize, Observable, of, tap } from 'rxjs';

@Component({
    selector: 'ifa-move-fax-modal',
    imports: [
        CommonModule,
        RadioButtonModule,
        FormsModule,
        SelectModule,
        ButtonModule,
        TagModule,
        ProgressSpinnerModule,
    ],
    templateUrl: './move-fax-modal.component.html',
    styleUrls: ['./move-fax-modal.component.scss']
})
export class MoveFaxModalComponent implements OnInit {
  selectedDestination: FaxDestination = FaxDestination.FaxFolder;
  selectedLocation: SelectItem<string> | null = null;
  message: { warning: boolean; text: string } | null = null;
  offshoreRestriction: RestrictionDto;
  faxData: {
    row: InboundFaxesTableModel;
    inbox: InboxDto;
  };
  isSubmitClicked = false;
  isLoading = true;

  public readonly ref: DynamicDialogRef = inject(DynamicDialogRef);
  public readonly refConfig: DynamicDialogConfig = inject(DynamicDialogConfig);
  public readonly FaxDestination: typeof FaxDestination = FaxDestination;
  public readonly InfoMessage: typeof InfoMessage = InfoMessage;
  private readonly inboundFaxApiService = inject(InboundFaxApiService);
  private readonly notificationService = inject(NotificationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly lookupService = inject(LookupService);

  public inboxOptions: SelectItem<string>[] = [];
  public folderOptions: SelectItem<string>[] = [];

  public get showFaxFolder() {
    return this.folderOptions.length > 1;
  }

  public async ngOnInit() {
    this.refConfig.closable = true;
    this.faxData = this.refConfig.data;
    await this.getInboxOptions();
    await this.getFolderOptions();
    this.initializeForm();
  }

  public selectDestination(): void {
    this.getMessage();
  }

  public submitClick(): void {
    this.isSubmitClicked = true;
    const fax: InboundFaxesTableModel = this.faxData.row;

    // select appropriate call
    let update$: Observable<InboundFaxDto>;
    if (this.selectedDestination === FaxDestination.Inbox) {
      update$ = this.inboundFaxApiService.updateInbox(this.selectedLocation.value, fax.inboundFaxId);
    } else {
      update$ = this.inboundFaxApiService.updateFolder(fax.inboundFaxId, {
        updateId: this.selectedLocation.value,
      });
    }

    // subscribe to call and handle response
    update$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.isSubmitClicked = false))
      )
      .subscribe({
        next: (updatedFax: InboundFaxDto): void => {
          this.notificationService.showSuccess(
            'Successfully Moved Fax',
            `Fax has been moved to ${this.selectedLocation.label}`
          );
          this.ref.close(updatedFax);
        },
        error: (): void => {
          this.notificationService.showError('Unable to Move Fax', 'An error has occurred. Please try again later.');
        },
      });
  }

  public cancelClick(): void {
    this.ref.close();
  }

  private async getInboxOptions(): Promise<void> {
    const inboxesDto = await this.lookupService.getInboxes();
    this.inboxOptions = inboxesDto
      .filter((x) => x.inboxId !== this.faxData.inbox.inboxId)
      .map((inbox: InboxDto) => ({ label: inbox.label, value: inbox.inboxId }));
  }

  private async getFolderOptions(): Promise<void> {
    const folderOptions = await this.lookupService.getFaxFolderReferences(this.faxData.inbox);

    if (folderOptions.length <= 1) {
      this.selectedDestination = FaxDestination.Inbox;
    }

    this.folderOptions = folderOptions.map((x) => ({ label: x.description, value: x.referenceByEntityId }));
  }

  private initializeForm(): void {
    (!!this.faxData.row.memberId
      ? // check restriction if user is assigned
        this.inboundFaxApiService.getBenefitPlanRestrictions(this.faxData.row.memberId).pipe(
          takeUntilDestroyed(this.destroyRef),
          tap((restriction: RestrictionDto): RestrictionDto => (this.offshoreRestriction = restriction))
        )
      : // do nothing
        of(null)
    ).subscribe({
      next: (): void => {
        this.getMessage();
        this.isLoading = false;
      },
      error: () =>
        this.notificationService.showError(
          'Unable to Load Benefit Plan Restrictions',
          'An error has occurred. Please try again later.'
        ),
    });
  }

  private getMessage(): void {
    // other folder
    if (this.selectedDestination === FaxDestination.FaxFolder) {
      // no assigned user
      if (!this.faxData.row.memberId) {
        this.message = { warning: true, text: 'Member must be assigned before moving to a fax folder.' };
        // restricted/unrestricted user plan
      } else {
        this.message = {
          warning: this.offshoreRestriction.restrictionStatus,
          text: this.offshoreRestriction.restrictionMessage,
        };
        // unrestricted user plan
      }
    } else if (!!this.faxData.row.memberId) {
      // other inbox but assigned user
      this.message = { warning: true, text: 'Member must be unassigned before moving to a fax inbox.' };
    } else {
      // other inbox without assigned user
      this.message = null;
    }
  }
}
